import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container, Typography, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, CircularProgress, Alert, Select, MenuItem,
  Box, Pagination, Chip, Button, IconButton, Tooltip, Tabs, Tab,
  Avatar
} from "@mui/material";
import {
  Person as PersonIcon,
  Badge as BadgeIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Flag as FlagIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  PictureAsPdf as PdfIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  AutoAwesome as AutoGenerateIcon,
  CloudUpload as CloudUploadIcon
} from "@mui/icons-material";
import { styled } from '@mui/material/styles';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ExperienceRequestList = () => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [companyId, setCompanyId] = useState(null);
  const [token, setToken] = useState(null);
  const [headerOption, setHeaderOption] = useState({}); 
  const limit = 5;

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
    
    setToken(token);
    const payload = JSON.parse(atob(token.split('.')[1]));
    setCompanyId(payload.company_id);
    
    fetchRequests();
  }, [page]);

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(
        `${API_URL}/api/experience?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setRequests(response.data.data || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (err) {
      setError("Failed to fetch experience requests. Please try again.");
      //console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (id, status, headerPref = 'with_header') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      setIsLoading(true);
      
      const response = await axios.put(
        `${API_URL}/api/experience/${id}/status`, 
        { status ,header_option: headerPref },
        {

          headers: { Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'}
        }
      );
      
      alert(`Experience request has been ${status}${response.data.certificateGenerated ? ' and certificate generated automatically' : ''}`);
      
      await fetchRequests();
      
    } catch (error) {
      //console.error("Error updating status:", error);
      alert("Failed to update status.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualGenerate = async (id,headerPref='with_header') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.post(
        `${API_URL}/api/experience/${id}/generate-certificate`,
        {
          header_option: headerPref
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      alert('Experience letter generated successfully!');
      fetchRequests();
    } catch (error) {
      //console.error('Error generating experience letter:', error);
      alert('Failed to generate experience letter. Please try again.');
    }
  };

  const handleFileUpload = async (event, id) => {
    const file = event.target.files[0];
    if (!file) return;
  
    const formData = new FormData();
    formData.append('certificate', file);
  
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.post(
        `${API_URL}/api/experience/${id}/certificate`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      alert('Experience letter uploaded and email sent successfully!');
      fetchRequests();
    } catch (error) {
      //console.error('Error uploading experience letter:', error);
      alert('Failed to upload experience letter. Please try again.');
    }
  };

  const handleViewCertificate = async (id, certificatePath) => {
    if (certificatePath && certificatePath.startsWith('http')) {
      window.open(certificatePath, "_blank");
      return;
    }

    try {
      const token = localStorage.getItem('token');
  
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const response = await axios.get(
        `${API_URL}/api/experience/certificates/${id}/view`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
  
      // Create blob URL
      const file = new Blob([response.data], {
        type: "application/pdf"
      });
  
      const fileURL = URL.createObjectURL(file);
  
      // Open PDF in new tab
      window.open(fileURL, "_blank");
  
    } catch (error) {
      //console.error("Error viewing certificate:", error);
  
      if (error.response?.status === 401) {
        alert("Unauthorized. Please login again.");
      } else {
        alert("Failed to view certificate.");
      }
    }
  };

const handleDownloadCertificate = async (id, employeeName) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }

    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    
    const response = await axios.get(
      `${API_URL}/api/experience/certificates/${id}/download`,
      { 
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Clean the employee name for filename
    const cleanName = employeeName
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_'); // Replace spaces with underscores
    
    link.setAttribute('download', `${cleanName}_ExperienceLetter.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    //console.error('Error downloading certificate:', error);
    alert('Failed to download certificate.');
  }
};

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'warning';
    }
  };

  return (
    <Container maxWidth="1500px" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, border: '2px solid', borderColor: 'grey.300', boxShadow: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
          Experience Letter Request List
        </Typography>
        
   


          <Box>
            {isLoading && <CircularProgress sx={{ display: "block", mx: "auto", my: 3 }} />}
            {error && <Alert severity="error">{error}</Alert>}

            {!isLoading && !error && (
              <>
                <TableContainer component={Paper} sx={{ mt: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {[
                          { icon: <PersonIcon />, text: "Name" },
                          { icon: <BadgeIcon />, text: "Employee ID" },
                          { icon: <EmailIcon />, text: "Candidate Email" },
                          { icon: <BusinessIcon />, text: "Department" },
                          { icon: <DescriptionIcon />, text: "Reason" },
                          { icon: <FlagIcon />, text: "Status" },
                          { icon: <PdfIcon />, text: "Certificate" },
                          { icon: <SettingsIcon />, text: "Actions" }
                        ].map((header, index) => (
                          <TableCell key={index}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {React.cloneElement(header.icon, { sx: { color: 'black', fontSize: 20 } })}
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'black' }}>
                                {header.text}
                              </Typography>
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {requests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>{request.name}</TableCell>
                          <TableCell>{request.employee_id}</TableCell>
                          <TableCell>{request.candidateEmail}</TableCell>
                          <TableCell>{request.department}</TableCell>
                          <TableCell>
                            <Tooltip title={request.reason}>
                              <Typography noWrap sx={{ maxWidth: 200 }}>
                                {request.reason?.substring(0, 50)}
                                {request.reason?.length > 50 ? '...' : ''}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={request.status || "Pending"}
                              color={getStatusColor(request.status)}
                              variant="outlined"
                              sx={{ 
                                fontWeight: 600,
                                borderWidth: 2,
                                minWidth: 100
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {request.certificate ? (
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="View Certificate">
                                  <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={() => handleViewCertificate(request.id, request.certificate)}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download Certificate">
                                  <IconButton 
                                    size="small" 
                                    color="secondary"
                                    onClick={() => handleDownloadCertificate(request.id,request.name)}
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                Not Generated
                              </Typography>
                            )}
                          </TableCell>
                     <TableCell>
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
    {/* Header Option Selector */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        Header:
      </Typography>
      <Select
        value={headerOption[request.id] || 'with_header'}
        onChange={(e) => setHeaderOption(prev => ({
          ...prev,
          [request.id]: e.target.value
        }))}
        size="small"
        sx={{ minWidth: 120 }}
      >
        <MenuItem value="with_header">With Header</MenuItem>
        <MenuItem value="without_header">Without Header</MenuItem>
      </Select>
    </Box>
    
    {/* Status Select */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        Status:
      </Typography>
      <Select
        value={request.status || "Pending"}
        onChange={(e) => handleStatusChange(
          request.id, 
          e.target.value, 
          headerOption[request.id] || 'with_header'
        )}
        size="small"
        sx={{ minWidth: 120 }}
      >
        <MenuItem value="Pending">Pending</MenuItem>
        <MenuItem value="Approved">Approve</MenuItem>
        <MenuItem value="Rejected">Reject</MenuItem>
      </Select>
    </Box>
    
    {request.status === 'Approved' && !request.certificate && (
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Tooltip title="Auto Generate Certificate">
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<AutoGenerateIcon />}
            onClick={() => handleManualGenerate(
              request.id, 
              headerOption[request.id] || 'with_header'
            )}
          >
            Generate
          </Button>
        </Tooltip>
        
        <Tooltip title="Manual Upload">
          <Button
            component="label"
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<CloudUploadIcon />}
          >
            Upload
            <VisuallyHiddenInput
              type="file"
              accept="application/pdf"
              onChange={(e) => handleFileUpload(e, request.id)}
            />
          </Button>
        </Tooltip>
      </Box>
    )}
  </Box>
</TableCell>

                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </>
            )}
          </Box>



      </Paper>
    </Container>
  );
};

export default ExperienceRequestList;